import { Head, Link, router, usePage } from "@inertiajs/react";
import {
  AlertTriangle,
  ArrowLeft,
  Building,
  CreditCard,
  FileText,
  Globe,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import InlineEditableField from "@/components/inline-editable-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import { shareOrganizationSupplierPath } from "@/routes";
import type { BreadcrumbItem, SharedData, Supplier } from "@/types";

interface SupplierShowProps {
  supplier: Supplier;
  categories?: { id: number; name: string }[];
  subcategories?: { id: number; name: string }[];
}

export default function SupplierShow({
  supplier,
  categories = [],
  subcategories = [],
}: SupplierShowProps) {
  const routes = useOrganizationRoutes();
  const { auth } = usePage<SharedData>().props;
  const currentOrganizationId = auth.current_organization?.id ?? null;
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const shareHref = shareOrganizationSupplierPath(routes.organizationSlug, supplier);

  const canEditTaxId =
    supplier.visibility === "private" &&
    supplier.owner_organization_id === currentOrganizationId;

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Proveedores",
      href: routes.suppliers.index(),
    },
    {
      title: supplier.name,
      href: "#",
    },
  ];
  const canShare =
    supplier.visibility === "private" &&
    supplier.owner_organization_id === currentOrganizationId;

  const handleShare = () => {
    setIsSharing(true);
    router.patch(shareHref, undefined, {
      onFinish: () => {
        setIsSharing(false);
        setIsShareDialogOpen(false);
      },
      onError: () => {
        toast.error("No se pudo compartir el proveedor");
      },
    });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Proveedor: ${supplier.name}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link href={routes.suppliers.index()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Proveedores
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              {supplier.name}
            </h1>
            <p className="text-muted-foreground">
              Gestiona la información del proveedor
            </p>
          </div>
          {canShare && (
            <Button onClick={() => setIsShareDialogOpen(true)}>
              <Globe className="mr-2 h-4 w-4" />
              Compartir proveedor
            </Button>
          )}
        </div>

        {/* General Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Información General
            </CardTitle>
            <CardDescription>
              Haz clic en el ícono de lápiz para editar cualquier campo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="Razón Social"
                value={supplier.name}
                field="name"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Building}
                placeholder="Razón social del proveedor"
              />

              <InlineEditableField
                label="Nombre Comercial"
                value={supplier.trade_name}
                field="trade_name"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Building}
                placeholder="Nombre comercial"
              />

              <InlineEditableField
                label="Código"
                value={supplier.code}
                field="code"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Building}
                placeholder="Código interno"
              />

              <InlineEditableField
                label="Categoría"
                value={supplier.category_id}
                field="category_id"
                type="select"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Building}
                options={categories.map((category) => ({
                  label: category.name,
                  value: category.id,
                }))}
                formatValue={() =>
                  supplier.category?.name ??
                  "No asignado"
                }
              />

              {!!supplier.category_id && (
                <InlineEditableField
                  label="Subcategoría"
                  value={supplier.subcategory_id}
                  field="subcategory_id"
                  type="select"
                  updateUrl={routes.suppliers.show(supplier)}
                  icon={Building}
                  options={subcategories?.map((category) => ({
                    label: category.name,
                    value: category.id,
                  }))}
                  formatValue={() =>
                    supplier.subcategory?.name ??
                    "No asignado"
                  }
                />
              )}

              <InlineEditableField
                label="Estado"
                value={supplier.status}
                field="status"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Building}
                placeholder="Estado del proveedor"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Información de Contacto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="Email"
                value={supplier.email}
                field="email"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Mail}
                placeholder="email@ejemplo.com"
              />

              <InlineEditableField
                label="Teléfono"
                value={supplier.phone}
                field="phone"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Phone}
                placeholder="+54 11 1234-5678"
              />

              <InlineEditableField
                label="Teléfono Adicional"
                value={supplier.additional_phone}
                field="additional_phone"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Phone}
                placeholder="+54 11 9876-5432"
              />

              <InlineEditableField
                label="Persona de Contacto"
                value={supplier.contact_person}
                field="contact_person"
                updateUrl={routes.suppliers.show(supplier)}
                icon={User}
                placeholder="Nombre del contacto"
              />

              <InlineEditableField
                label="Sitio Web"
                value={supplier.website}
                field="website"
                updateUrl={routes.suppliers.show(supplier)}
                icon={Globe}
                placeholder="https://www.ejemplo.com"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Dirección
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="Dirección"
                value={supplier.address}
                field="address"
                updateUrl={routes.suppliers.show(supplier)}
                icon={MapPin}
                placeholder="Dirección completa"
              />

              <InlineEditableField
                label="Ciudad"
                value={supplier.city}
                field="city"
                updateUrl={routes.suppliers.show(supplier)}
                icon={MapPin}
                placeholder="Ciudad"
              />

              <InlineEditableField
                label="Código Postal"
                value={supplier.postal_code}
                field="postal_code"
                updateUrl={routes.suppliers.show(supplier)}
                icon={MapPin}
                placeholder="Código postal"
              />

              <InlineEditableField
                label="Provincia"
                value={supplier.state}
                field="state"
                updateUrl={routes.suppliers.show(supplier)}
                icon={MapPin}
                placeholder="Provincia"
              />

              <InlineEditableField
                label="País"
                value={supplier.country}
                field="country"
                updateUrl={routes.suppliers.show(supplier)}
                icon={MapPin}
                placeholder="País"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tax Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Información Fiscal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="CUIT"
                value={supplier.tax_id}
                field="tax_id"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="XX-XXXXXXXX-X"
                disabled={!canEditTaxId}
              />

              <InlineEditableField
                label="Condición IVA"
                value={supplier.vat_condition}
                field="vat_condition"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="Responsable Inscripto"
              />

              <InlineEditableField
                label="Inscripción Ganancias"
                value={supplier.income_tax_registration}
                field="income_tax_registration"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="Número de inscripción"
              />

              <InlineEditableField
                label="Categoría Ganancias"
                value={supplier.income_tax_category}
                field="income_tax_category"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="Categoría"
              />

              <InlineEditableField
                label="RG 830"
                value={supplier.rg_830}
                field="rg_830"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="RG 830"
              />

              <InlineEditableField
                label="RG 18"
                value={supplier.rg_18}
                field="rg_18"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="RG 18"
              />

              <InlineEditableField
                label="Categoría IIBB"
                value={supplier.gross_income_category}
                field="gross_income_category"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="Categoría IIBB"
              />

              <InlineEditableField
                label="Nro. Inscripción IIBB"
                value={supplier.gross_income_registration_number}
                field="gross_income_registration_number"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="Número de inscripción IIBB"
              />

              <InlineEditableField
                label="Condición IIBB"
                value={supplier.gross_income_condition}
                field="gross_income_condition"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="Condición IIBB"
              />

              <InlineEditableField
                label="Embargo IIBB"
                value={supplier.gross_income_embargo}
                field="gross_income_embargo"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="Estado de embargo"
              />

              <InlineEditableField
                label="Saldo Embargo IIBB"
                value={supplier.gross_income_embargo_balance?.toString()}
                field="gross_income_embargo_balance"
                updateUrl={routes.suppliers.show(supplier)}
                icon={CreditCard}
                placeholder="0.00"
              />
            </div>
          </CardContent>
        </Card>

        {/* Business Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Información Comercial
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="Actividad Principal"
                value={supplier.main_activity}
                field="main_activity"
                updateUrl={routes.suppliers.show(supplier)}
                icon={FileText}
                placeholder="Descripción de la actividad principal"
              />

              <InlineEditableField
                label="Condición de Compra"
                value={supplier.purchase_condition}
                field="purchase_condition"
                updateUrl={routes.suppliers.show(supplier)}
                icon={FileText}
                placeholder="Condiciones de compra"
              />

              <InlineEditableField
                label="Cuenta Contable"
                value={supplier.accounting_account}
                field="accounting_account"
                updateUrl={routes.suppliers.show(supplier)}
                icon={FileText}
                placeholder="Código de cuenta contable"
              />

              <InlineEditableField
                label="Crítico"
                value={supplier.critical ? "Sí" : "No"}
                field="critical"
                updateUrl={routes.suppliers.show(supplier)}
                icon={AlertTriangle}
                placeholder="Sí/No"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notes and Additional Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Notas y Observaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6">
              <InlineEditableField
                label="Observaciones"
                value={supplier.observations}
                field="observations"
                updateUrl={routes.suppliers.show(supplier)}
                icon={FileText}
                placeholder="Observaciones generales"
              />

              <InlineEditableField
                label="Información Auxiliar"
                value={supplier.supplier_auxiliary_info}
                field="supplier_auxiliary_info"
                updateUrl={routes.suppliers.show(supplier)}
                icon={FileText}
                placeholder="Información auxiliar del proveedor"
              />

              <InlineEditableField
                label="Notas"
                value={supplier.notes}
                field="notes"
                updateUrl={routes.suppliers.show(supplier)}
                icon={FileText}
                placeholder="Notas adicionales"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Compartir proveedor públicamente</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Una vez compartido, este proveedor será visible para todas las
                organizaciones que tengan habilitado el catálogo público.
              </p>
              <p>
                Ya no podrás editarlo directamente. Cualquier cambio deberá
                hacerse como override local.
              </p>
              <p className="font-medium text-amber-600 dark:text-amber-400">
                Esta acción es irreversible.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSharing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleShare} disabled={isSharing}>
              {isSharing ? "Compartiendo..." : "Compartir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
